<template>
  <v-sheet class="w-100 fill-height border rounded" elevation="1">
    <v-row v-if="isConverting" no-gutters>
      <v-col cols="12" class="d-flex justify-center align-center flex-column fill-height">
        <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
        <p class="text-body-1 font-weight-medium mt-2">
          {{ $t('messages.convertingYourCreative') }}
        </p>
      </v-col>
    </v-row>
    <v-row v-else-if="isConvertingError || emptyProducts" no-gutters class="fill-height">
      <v-col cols="12" class="d-flex justify-center align-center flex-column">
        <v-icon size="50">mdi-alert-circle-outline</v-icon>
        <p class="text-body-1 font-weight-medium mt-2" v-if="isConvertingError">
          {{ $t('messages.errorConvertingCreative') }}
        </p>
        <p class="text-body-1 font-weight-medium mt-2" v-else>
          {{ $t('messages.noProductsPresent') }}
        </p>
      </v-col>
    </v-row>
    <v-form v-else ref="form" @submit.prevent="submitForm" class="h-100 overflow-y-auto pa-4 pb-2">
      <v-row class="ma-0 pb-0">
        <v-col class="ma-0 pa-0 py-2 pb-4 d-flex align-center">
          <v-select
            v-model="selectedAdSizeCreativeType"
            :items="adSizes"
            item-title="displayName"
            item-value="creativeType"
            label="Ad Size"
            variant="outlined"
            hide-details
            style="min-width: 180px"
            :loading="adSizeConfigStore.isLoading"
            :disabled="adSizeConfigStore.isLoading"
          />
        </v-col>
      </v-row>
      <v-row class="ma-0 pb-0" v-if="!showBrandAddition">
        <v-btn
          icon="mdi-plus"
          rounded="0"
          variant="plain"
          v-if="
            appStore.selectedDesignFlow.value === designFlows.upload.value ||
            appStore.selectedDesignFlow.value === designFlows.convert.value
          "
          size="40"
          class="mt-2 pr-3"
          density="comfortable"
          @click="toggleBrandSelection"
        ></v-btn>
        <v-menu
          v-if="!isEmbedUser"
          v-model="menuOpen"
          :close-on-content-click="false"
          offset-y
          max-height="300"
          eager
          location="bottom"
          @update:modelValue="setSelectedBrand"
        >
          <template #activator="{ props }">
            <v-text-field
              v-model="searchInput"
              v-bind="props"
              :label="$t('labels.brand')"
              @blur="onBlur"
              :rules="skipValidation ? [] : [rules.required]"
              variant="outlined"
              prepend-inner-icon="mdi-magnify"
              append-inner-icon="mdi-menu-down"
              class="brands_selection"
              :hide-details="menuOpen"
              :disabled="isFetchingLabels"
            />
          </template>

          <v-list class="pa-0 pl-2 brands-list" color="primary">
            <v-infinite-scroll :items="filteredBrands" max-height="40vh" @load="loadBrands">
              <template v-if="filteredBrands.length === 0 && noMoreBrands">
                <v-list-item>
                  <v-list-item-title>No brands</v-list-item-title>
                </v-list-item>
              </template>
              <template v-else v-for="brand in filteredBrands" :key="brand.id">
                <v-list-item
                  :active="brandStore.selectedBrand?.id === brand.id"
                  :disabled="brandStore.selectedBrand?.id === brand.id"
                  @click="selectBrand(brand)"
                  :class="[
                    'brand-item',
                    'mr-2',
                    { 'opacity-100': brandStore.selectedBrand?.id === brand.id },
                  ]"
                >
                  <template #prepend>
                    <v-img
                      v-if="brand.logoUrls?.[0]"
                      :src="brand.logoUrls[0].logoUrl"
                      width="35"
                      height="35"
                      alt="Brand Logo"
                      contain
                      crossorigin="anonymous"
                      class="mr-3"
                    />
                    <v-icon v-else>mdi-shape</v-icon>
                  </template>
                  <v-list-item-title>{{ truncateBrand(brand.name, 25) }}</v-list-item-title>
                  <template v-slot:append="{ isActive }">
                    <v-icon v-if="isActive" icon="mdi-check-circle" color="primary"></v-icon>
                  </template>
                </v-list-item>
              </template>
              <template #loading>
                <div class="text-center">
                  <v-progress-circular
                    v-if="!noMoreBrands"
                    color="primary"
                    indeterminate
                  ></v-progress-circular>
                </div>
              </template>
            </v-infinite-scroll>
          </v-list>
        </v-menu>
      </v-row>
      <div
        :class="['d-flex', 'justify-space-between', { 'mt-5': menuOpen }]"
        v-if="!(showBrandAddition || isEmbedUser)"
      >
        <v-card
          v-for="(option, index) in Object.values(designFlows)"
          :key="option.value"
          :class="{
            'text-primary': appStore.selectedDesignFlow.value == option.value,
            'mr-3': index !== Object.values(designFlows).length - 1,
          }"
          :style="
            appStore.selectedDesignFlow.value === option.value
              ? {
                  'border-radius': '16px',
                  'background-color': 'rgba(var(--v-theme-primary), 0.1)',
                }
              : {}
          "
          class="mb-4 cursor-pointer text-center"
          style="width: 48%; border-radius: 8px"
          variant="outlined"
          elevation="2"
          @click="setDesignFlow(option)"
        >
          <v-card-text>
            {{ $t(option.label) }}
            <v-card-subtitle class="text-wrap mt-4" v-if="option.description">
              {{ $t(option.description) }}
            </v-card-subtitle>
          </v-card-text>
        </v-card>
      </div>
      <v-row
        v-if="
          (appStore.selectedDesignFlow.value === designFlows.upload.value ||
            appStore.selectedDesignFlow.value === designFlows.convert.value) &&
          !showBrandAddition
        "
      >
        <v-col cols="12" class="d-flex justify-start text-uppercase">
          <p>Creative:</p>
        </v-col>
        <v-col cols="auto" class="mb-3">
          <!-- Upload Card -->
          <v-card
            variant="outlined"
            elevation="2"
            class="rounded-lg d-flex flex-column align-center justify-center"
            height="150"
            width="130"
            v-if="!creativePreview"
            @click="triggerCreativeFileInput"
          >
            <v-icon size="36">mdi-upload</v-icon>

            <!-- Hidden File Input -->
            <v-file-input
              v-show="false"
              v-model="creativeFile"
              accept="image/png, image/jpeg, image/avif"
              variant="outlined"
              dense
              density="comfortable"
              hide-details
              :multiple="false"
              class="mb-4"
              @update:modelValue="handleFileChange"
              ref="fileInputCreative"
            />
          </v-card>

          <!-- Preview Card -->
          <v-card
            height="150"
            width="130"
            variant="outlined"
            elevation="2"
            class="rounded-lg"
            v-else
          >
            <v-row class="ma-0">
              <v-col>
                <v-btn
                  icon="mdi-close"
                  size="16"
                  variant="plain"
                  @click="removeFile"
                  class="position-absolute top-0 right-0 pr-1"
                ></v-btn>
              </v-col>
            </v-row>
            <v-img :src="creativePreview" height="106" contain></v-img>
          </v-card>
          <p v-if="creativeFileError" class="text-start text-error text-caption my-2">
            {{ creativeFileError }}
          </p>
        </v-col>
      </v-row>
      <v-container
        v-if="appStore.selectedDesignFlow.value === designFlows.generate.value"
        :class="['text-start', 'pa-0', { 'mt-2': isEmbedUser }]"
      >
        <div :class="['mb-3', 'mt-n2']">
          {{ $t('labels.products') }}
        </div>
        <v-row class="ml-0">
          <v-col cols="12" md="6" lg="3" xl="2" class="pl-0 py-1">
            <v-menu
              location="right top"
              :close-on-content-click="false"
              width="360"
              v-model="productMenu"
              @update:modelValue="onMenuChange"
            >
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  :class="[{ 'elevation-1': productStore.products.length && !isProductsLoading }]"
                  size="x-large"
                  prepend-icon="mdi-plus"
                  height="56"
                  block
                  :disabled="productStore.products.length == 0 ? true : false || isProductsLoading"
                >
                  {{ $t('labels.add') }}
                  <template #append>
                    <v-progress-circular
                      v-if="isProductsLoading"
                      indeterminate
                      size="small"
                    ></v-progress-circular>
                  </template>
                </v-btn>
              </template>
              <v-card>
                <v-card-title class="pt-4">
                  <v-text-field
                    v-model="productSearch"
                    :placeholder="$t('labels.search')"
                    prepend-inner-icon="mdi-magnify"
                    bg-color="grey-lighten-3"
                    variant="solo"
                    density="compact"
                    class="border-0"
                    clearable
                    flat
                    hide-details
                  ></v-text-field>
                </v-card-title>
                <v-card-text class="products-list pr-3">
                  <v-expansion-panels v-model="categoryPanel" flat multiple>
                    <v-expansion-panel
                      v-for="(category, index) in productCategory"
                      :key="category"
                      bg-color="grey-lighten-3"
                      class="pa-0"
                      :value="index"
                      static
                    >
                      <v-expansion-panel-title class="px-4 font-weight-medium">
                        {{ category }}
                      </v-expansion-panel-title>
                      <v-expansion-panel-text class="category-list">
                        <v-list
                          max-height="226"
                          bg-color="grey-lighten-3"
                          class="pt-0"
                          color="primary"
                        >
                          <v-list-item
                            v-for="product in filteredProducts.filter(
                              (product) => product.parentCategory == category,
                            )"
                            :key="product.id"
                            :class="['pa-1', 'mb-1', 'mx-2', {}]"
                            rounded
                            @click="toggleProduct(product)"
                            :active="
                              productStore.selectedProducts.find((p) => p.id == product.id)
                                ? true
                                : false
                            "
                            :disabled="
                              productStore.selectedProducts.length == MAXIMUM_SELECTABLE_PRODUCTS &&
                              !productStore.selectedProducts.find((p) => p.id == product.id)
                            "
                            v-slot="{ isActive }"
                          >
                            <v-row>
                              <v-col cols="3">
                                <v-list-item-media class="bg-grey-lighten-2 rounded pa-1">
                                  <v-img
                                    :src="product.productUrl"
                                    width="48"
                                    height="40"
                                    crossorigin="anonymous"
                                  ></v-img>
                                </v-list-item-media>
                              </v-col>
                              <v-col :cols="isActive ? 7 : 9" class="pl-0">
                                <v-tooltip :open-delay="500" :max-width="400">
                                  <template #activator="{ props }">
                                    <v-list-item-title v-bind="props">
                                      {{ product.parentProductName }}
                                    </v-list-item-title>
                                  </template>
                                  <span>{{ product.parentProductName }}</span>
                                </v-tooltip>
                                <v-list-item-subtitle class="product_quantitiy">
                                  {{ product.weightInGrams + product.unitOfMeasure }}
                                </v-list-item-subtitle>
                              </v-col>
                              <v-col v-if="isActive" cols="2" align-self="center">
                                <v-icon icon="mdi-check-circle" color="primary"></v-icon>
                              </v-col>
                            </v-row>
                          </v-list-item>
                        </v-list>
                      </v-expansion-panel-text>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-col>
          <v-col
            v-for="product in productStore.selectedProducts"
            :key="product.id"
            cols="12"
            md="6"
            lg="3"
            xl="3"
            class="pl-0 pr-3 py-1"
          >
            <v-card>
              <v-card-text class="pa-1">
                <v-row no-gutters class="flex-nowrap">
                  <v-col cols="auto">
                    <v-img
                      :src="product.productUrl"
                      width="45"
                      crossorigin="anonymous"
                      class="bg-grey-lighten-2 rounded mr-1"
                    ></v-img>
                  </v-col>
                  <v-col class="product-details">
                    <v-tooltip :open-delay="500" :max-width="400">
                      <template #activator="{ props }">
                        <v-list-item-title v-bind="props">
                          {{ product.parentProductName }}
                        </v-list-item-title>
                      </template>
                      <span>{{ product.parentProductName }}</span>
                    </v-tooltip>
                    <v-list-item-subtitle>
                      <v-row>
                        <v-col class="product_quantitiy text-truncate">
                          {{ product.weightInGrams + product.unitOfMeasure }}
                        </v-col>
                        <v-col cols="auto">
                          <v-btn
                            variant="text"
                            icon="mdi-close"
                            size="small"
                            density="compact"
                            @click="deselectProduct(product)"
                          ></v-btn>
                        </v-col>
                      </v-row>
                    </v-list-item-subtitle>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-container
        v-if="appStore.selectedDesignFlow.value === designFlows.generate.value"
        class="px-0"
      >
        <v-row no-gutters>
          <v-col cols="12" class="text-subtitle-2 text-medium-emphasis text-start pb-0">
            {{ t('labels.discountType') }}
          </v-col>
          <v-col cols="6" class="pt-0">
            <v-select
              v-model="selectedDiscountType"
              :items="Object.values(discountTypesInfo)"
              @focus="productMenu = false"
              item-title="label"
              item-value="value"
              variant="outlined"
              density="comfortable"
              hide-details
            />
          </v-col>
          <v-col cols="3" v-if="isDiscountEnabled" class="pt-0">
            <v-btn-toggle v-model="selectedDiscountVariation" divided mandatory variant="outlined">
              <v-btn
                v-for="option in Object.values(discountVariationsInfo)"
                :key="option.value"
                :value="option.value"
                variant="outlined"
              >
                {{ option.label }}
              </v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="3" v-if="isDiscountEnabled" class="pt-0">
            <v-text-field
              v-model="discountValue"
              variant="outlined"
              density="comfortable"
              placeholder="Discount"
              type="number"
              min="0"
              :max="selectedDiscountType === discountTypes.percentage ? 100 : null"
              hide-details="auto"
              style="height: 100%"
              ref="discountValueField"
              :rules="discountValueRules"
              @keypress="blockNegativeSign"
              @paste="onPasteNumberOnly"
            >
              <template #append-inner>
                <v-icon v-if="selectedDiscountType == discountTypes.percentage" icon="mdi-percent" />
                <span v-else class="currency-symbol">{{ appStore.profileCurrencySymbol }}</span>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row v-if="isDiscountEnabled" class="mt-0">
          <v-col cols="12" class="text-subtitle-2 text-medium-emphasis text-start pb-0">
            {{ t('labels.discountCode') }}
          </v-col>
          <v-col cols="12" class="pt-0">
            <v-text-field
              v-model="upperCaseCouponCode"
              density="comfortable"
              variant="outlined"
              hide-details="auto"
              :placeholder="t('messages.enterDiscountCode')"
              counter
              maxlength="10"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-container>

      <!--Brief And Labels-->
      <v-row v-if="appStore.selectedDesignFlow.value === designFlows.generate.value">
        <!-- Brief-->
        <v-col
          cols="12"
          class="text-subtitle-2 text-medium-emphasis text-start pb-0 pt-2"
          :class="[{ 'pt-0 mt-n3': isDiscountEnabled }]"
        >
          {{ t('labels.brief') }}
        </v-col>
        <v-col cols="12" class="pb-0 pt-1">
          <v-textarea
            v-model="brandStore.activeLabel.brief"
            variant="outlined"
            :placeholder="$t('labels.writeABrief')"
            :rules="[rules.maxBrief]"
            density="comfortable"
            rows="1"
            auto-grow
            hide-details="auto"
            @input="onBriefInput"
          />
        </v-col>

        <!--Labels-->
        <v-col cols="12" class="pa-0">
          <v-chip-group
            column
            mandatory
            v-if="brandStore.labelsAndBriefs"
            v-model="localLabelBrief"
            :selected-class="isCustomBrief ? '' : 'text-primary'"
          >
            <!-- Show actual label chips with loading effect once data is available -->
            <v-chip
              v-for="(item, index) in brandStore.labelsAndBriefs"
              :key="index"
              :value="item"
              :text="item.label"
              variant="outlined"
              class="text-medium-emphasis ma-1 justify-center"
              min-width="100"
              @click="isCustomBrief = false"
            >
            </v-chip>
          </v-chip-group>
        </v-col>
      </v-row>
      <div v-if="showBrandAddition">
        <v-row no-gutters>
          <v-col cols="12" class="my-0 py-0">
            <v-text-field
              label="Enter Brand Name"
              variant="outlined"
              v-model="brandName"
            ></v-text-field>
          </v-col>
          <v-col cols="3" class="mt-1 pt-1">
            <v-card
              height="116px"
              width="150px"
              elevation="2"
              variant="outlined"
              class="d-flex align-center justify-center rounded-lg"
              @click="triggerFileInput"
              :disabled="isFileUploading"
            >
              <v-icon size="36" v-if="!isFileUploading">mdi-upload</v-icon>
              <v-progress-circular
                indeterminate
                color="primary"
                size="36"
                v-if="isFileUploading"
              ></v-progress-circular>
            </v-card>
            <v-file-input
              v-model="selectedBrandLogo"
              ref="fileInput"
              accept="image/jpeg, image/png, image/avif"
              variant="outlined"
              density="comfortable"
              class="d-none custom-brand"
              hide-details
              :multiple="false"
              :error-messages="fileError"
              @change="handleFileUpload"
            ></v-file-input>
          </v-col>
          <v-col
            cols="3"
            v-for="(value, index) in brandLogos"
            :key="index"
            class="mt-1 pt-1"
            elevation="3"
          >
            <v-card
              height="116px"
              width="150px"
              variant="outlined"
              elevation="2"
              class="rounded-lg"
            >
              <v-img :src="value" height="116px" width="150px"></v-img>
              <v-btn
                icon="mdi-close"
                size="16"
                variant="plain"
                @click="removeLogo(value)"
                class="position-absolute top-0 right-0 pr-1"
              ></v-btn>
            </v-card>
          </v-col>
        </v-row>
        <p v-if="brandUploadError" class="text-start text-error text-caption my-2">
          {{ brandUploadError }}
        </p>
        <v-row class="my-3 py-0">
          <v-col class="d-flex justify-end my-0 py-0">
            <v-btn variant="text" color="grey" @click="toggleBrandSelection">Close</v-btn>
            <v-btn
              variant="tonal"
              color="primary"
              :disabled="brandName === '' || brandLogos.length === 0"
              @click="addNewBrand"
            >
              Save
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-form>
  </v-sheet>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed, onUnmounted, inject } from 'vue'
import { VForm } from 'vuetify/components/VForm'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import getSymbolFromCurrency from 'currency-symbol-map'

import { useUploadedCreativeStore } from '@/stores/uploadedCreative'
import { useAppStore } from '@/stores/app'
import { useBrandStore } from '@/stores/brand'
import { usePlacementStore } from '@/stores/placement'
import { useProductStore } from '@/stores/product'
import { useEnhancementsStore } from '@/stores/enhancement'
import { useCanvasStore } from '@/stores/canvas'
import { useCreativeStore } from '@/stores/creative'
import { getErrorMessage, getImageDimensionsFromFile, uploadToS3 } from '@/helpers/commonHelper'
import type { Brand, LabelBriefPair } from '@/types/brand'
import ProductService from '@/services/productService'
import BrandService from '@/services/brandService'
import PlacementService from '@/services/placementService'
import designFlows from '@/constants/designFlows'
import { autoFixCreative } from '@/helpers/creativeHelper'
import { VTextField, type VFileInput } from 'vuetify/components'
import type { DesignFlow } from '@/types/designFlow'
import type { FooterConfigProvider } from '@/types/footerConfig'
import s3Folders from '@/constants/s3Folders'
import { ProductProcessingStatus } from '@/constants/productProcessingType'
import { debounce } from 'lodash'
import { useAppTheme } from '@/composables/useTheme'
import { applyTheme } from '@/utils/themeManager'
import type { Product } from '@/types/product'
import { discountTypes, discountVariations } from '@/constants/canvas'
import { MarketingLabelsBriefs } from '@/constants/briefsLabels'
import { transparentBackground } from '@/helpers/backgroundHelper'
import ProductsNotPresentException from '@/errors/ProductsNotPresentException'
import { useAutofixStore } from '@/stores/autofix'
import { useAdSizeConfigStore } from '@/stores/adSizeConfig'
import type { AdSizeSummary } from '@/types/adSizeConfig'

const { t } = useI18n()
const router = useRouter()
const uploadedCreativeStore = useUploadedCreativeStore()
const appStore = useAppStore()
const brandStore = useBrandStore()
const placementStore = usePlacementStore()
const productStore = useProductStore()
const enhancementStore = useEnhancementsStore()
const canvasStore = useCanvasStore()
const creativeStore = useCreativeStore()
const autofixStore = useAutofixStore()
const adSizeConfigStore = useAdSizeConfigStore()
const { theme } = useAppTheme()

const productService = new ProductService()
const brandService = new BrandService()
const placementService = new PlacementService()

const { footerConfig, setFooterConfig } = inject<FooterConfigProvider>('footerConfig')!
const notify = inject('notify') as Function

const rules = {
  required: (value: string | null) => !!value || t('errors.requiredField'),
  maxBrief: (value: string) => value.length <= 150 || t('errors.maxBrief'),
}

const discountTypesInfo = {
  percentage: {
    label: 'Percentage',
    value: discountTypes.percentage,
  },
  fixedAmount: {
    label: 'Fixed Amount',
    value: discountTypes.fixed,
  },
  noDiscount: {
    label: 'No Discount',
    value: discountTypes.none,
  },
}

const discountVariationsInfo = {
  upto: {
    label: 'UP TO',
    value: discountVariations.upto,
  },
  flat: {
    label: 'FLAT',
    value: discountVariations.flat,
  },
}

const MAXIMUM_SELECTABLE_PRODUCTS = 20

const allowedTypes = ['image/jpeg', 'image/png', 'image/avif']
const isLoading = ref(false)
const isConverting = ref(false)
const isConvertingError = ref(false)
const emptyProducts = ref(false)
const autofixError = ref(false)
const disableRetry = ref(false)
const creativeFile = ref<File | null>(null)
const creativePreview = ref<string | null>(null)
const form = ref<InstanceType<typeof VForm> | null>(null)
const searchInput = ref('')
const brandSearchTerm = ref('')
const creativeFileError = ref<string | null>(null)
const MAX_SIZE_MB = 10
const brandUploadError = ref<string | null>(null)
const menuOpen = ref(false)
const noMoreBrands = ref(false)
const skipValidation = ref(false)
const skipSearchUpdate = ref(false)
const isFetchingLabels = ref(false)
const productSearch = ref()
const productMenu = ref(false)
const categoryPanel = ref([0])
const isDiscountFieldValid = ref<boolean>(true)
const discountMenu = ref(false)
const isProductsLoading = ref(false)
const discountValueField = ref<VTextField>()
const localLabelBrief = ref<LabelBriefPair>({ label: '', brief: '' })
const isCustomBrief = ref(false)
const tempDiscountDetails = ref<{
  type: string
  value: string
  couponCode: string
  variation: string
}>({
  type: discountTypes.none,
  value: '',
  couponCode: '',
  variation: discountVariations.upto,
})
const showBrandAddition = ref<boolean>(false)
const fileInput = ref<HTMLInputElement | null>(null)
const fileInputCreative = ref<HTMLInputElement | null>(null)
const selectedBrandLogo = ref<File | null>(null)
const isFileUploading = ref<boolean>(false)
const fileError = ref<string | null>(null)
const brandLogos = ref<string[]>([])
const brandName = ref<string>('')
const brandNameError = ref<string | null>(null)
const selectedAdSizeCreativeType = ref<string>('')
const adSizes = ref<AdSizeSummary[]>([])

const selectedDiscountVariation = computed({
  get: () => canvasStore.discountVariation,
  set: (value) => {
    canvasStore.setDiscountVariation(value)
  },
})

const discountValue = computed({
  get: () => canvasStore.discountValue,
  set: (value) => {
    const cleanedValue = value.toString().replace(/\+/g, '')
    canvasStore.setDiscountValue(cleanedValue)
  },
})

const selectedDiscountType = computed({
  get: () => canvasStore.discountType,
  set: (value) => {
    if (value == discountTypes.none) {
      canvasStore.setDiscountVariation(discountVariations.upto)
      canvasStore.setDiscountValue('')
      canvasStore.setRawCouponCode('')
    }
    canvasStore.setDiscountType(value)
  },
})

const isEmbedUser = computed(() => {
  return appStore.embedUser
})

const isFormInvalid = computed(() => {
  if (!form.value) return true

  const isFileInvalid =
    appStore.selectedDesignFlow.value !== designFlows.generate.value &&
    (!creativeFile.value || creativeFileError.value)

  return Boolean(
    !form.value.isValid ||
      !placementStore.selectedPublishedVariant ||
      isFileInvalid ||
      (appStore.selectedDesignFlow.value === designFlows.generate.value &&
        !productStore.selectedProducts.length) ||
      isFetchingLabels.value,
  )
})

const isDiscountEnabled = computed(() => {
  return canvasStore.discountType != discountTypes.none
})

const upperCaseCouponCode = computed({
  get: () => canvasStore.rawCouponCode.toUpperCase(),
  set: (value: string) => {
    canvasStore.setRawCouponCode(value.toUpperCase())
  },
})

const discountValueRules = computed(() => [
  (v: number) => {
    if (!v) {
      isDiscountFieldValid.value = false
    } else if (v <= 0) {
      isDiscountFieldValid.value = false
      return 'Value must be greater than 0'
    } else if (
      discountMenu.value &&
      tempDiscountDetails.value.type === discountTypes.percentage &&
      v > 90
    ) {
      isDiscountFieldValid.value = false
      return 'Valid Range 0-90'
    } else if (
      !discountMenu.value &&
      selectedDiscountType.value === discountTypes.percentage &&
      v > 90
    ) {
      isDiscountFieldValid.value = false
      return 'Valid Range 0-90'
    } else {
      isDiscountFieldValid.value = true
    }
    return true
  },
])

const filteredProducts = computed(() => {
  if (productSearch.value) {
    return productStore.products.filter((product) =>
      product.parentProductName.toLowerCase().includes(productSearch.value.trim().toLowerCase()),
    )
  }

  return productStore.products
})
const filteredBrands = computed(() => {
  if (
    appStore.selectedDesignFlow.value === designFlows.upload.value ||
    appStore.selectedDesignFlow.value === designFlows.convert.value
  ) {
    return brandStore.brands
  } else {
    return brandStore.brands.slice(brandStore.addedBrandsCount, brandStore.brands.length)
  }
})
const productCategory = computed(() => {
  return [
    ...new Map(filteredProducts.value.map((product) => [product.parentCategory, product])).keys(),
  ]
})

const validateDiscount = (): boolean => {
  if (appStore.selectedDesignFlow.value !== designFlows.generate.value) return true
  if (isDiscountFieldValid.value === false && isDiscountEnabled.value) {
    notify(t('errors.pleaseEnterValidDiscount'), 'error')
    return false
  }
  return true
}

const toggleProduct = (product: Product) => {
  if (productStore.selectedProducts.find((p) => p.id == product.id)) {
    deselectProduct(product)
  } else {
    productStore.selectedProducts.push(product)
  }
}

const deselectProduct = (product: Product) => {
  productStore.selectedProducts = productStore.selectedProducts.filter((p) => p.id != product.id)
}

const blockNegativeSign = (e: KeyboardEvent) => {
  if (e.key === '-' || e.key == '+' || e.key == 'e' || e.key == 'E') {
    e.preventDefault()
  }
}

const onPasteNumberOnly = (event: ClipboardEvent) => {
  const pasted = event.clipboardData?.getData('text') ?? ''
  // If pasted text contains e, E, - or +, block the paste
  if (/[eE\-\+]/.test(pasted)) {
    event.preventDefault()
  }
}

const onBlur = (event: any) => {
  const isBrandItem = event.relatedTarget?.classList.contains('brand-item')
  if (isBrandItem) {
    skipValidation.value = true
  }
}

const loadBrands = async ({ done }: any) => {
  if (
    appStore.selectedDesignFlow.value === designFlows.upload.value ||
    appStore.selectedDesignFlow.value === designFlows.convert.value
  ) {
    if (brandStore.totalBrandsCount + brandStore.addedBrandsCount == brandStore.brands.length) {
      done('ok')
      noMoreBrands.value = true
      return
    }
  } else {
    if (brandStore.totalBrandsCount == filteredBrands.value.length) {
      done('ok')
      noMoreBrands.value = true
      return
    }
  }

  noMoreBrands.value = false

  try {
    brandStore.currentPageNumber += 1
    const searchParams = {
      ...brandStore.filters,
      pageNumber: brandStore.currentPageNumber,
      ...(brandSearchTerm.value !== '' ? { name: brandSearchTerm.value.trim() } : {}),
    }
    const { brands, totalFilteredCount } = await brandService.getBrands(
      appStore.selectedBusinessProfileId,
      searchParams,
    )

    brandStore.appendBrands(brands)
    brandStore.totalBrandsCount = totalFilteredCount
    if (!brands.length) {
      noMoreBrands.value = true
    }

    done('ok')

    // Update the brands list based on last searched value
    const isEmptySearch = brandSearchTerm.value.trim() == '' && !searchParams.name
    const isDifferent = searchParams.name != brandSearchTerm.value.trim()

    if (!isEmptySearch && isDifferent) {
      brandStore.clearBrands()
    }
  } catch (error) {
    console.error(error)
    done('error')
  }
}

const setSelectedBrand = (value: any) => {
  if (!value && brandStore.selectedBrand?.name && (!searchInput.value || searchInput.value == '')) {
    skipSearchUpdate.value = true
    searchInput.value = brandStore.selectedBrand?.name
  }
}

const selectBrand = async (brand: Brand) => {
  brandStore.setSelectedBrand(brand)
  skipSearchUpdate.value = true
  searchInput.value = brand.name
  brandSearchTerm.value = ''
  skipValidation.value = false
  menuOpen.value = false
}

const onBriefInput = debounce(() => {
  const currentBrief = brandStore.activeLabel.brief
  if (!localLabelBrief.value || localLabelBrief.value.brief !== currentBrief) {
    isCustomBrief.value = true
  } else {
    isCustomBrief.value = false
  }
}, 200)

const setPlacement = async (newCreativeType: string) => {
  if (!newCreativeType || !appStore.selectedBusinessProfileId) return

  try {
    const placement = placementStore.placements.find((p) => p.creativeType == newCreativeType)

    if (placement) {
      placement.backgroundImages.unshift(transparentBackground)
      placementStore.setPlacement(placement)
    }

    if (!placement?.publishedVariants.length) {
      throw new Error('No canvas jsons found')
    }
  } catch (error) {
    console.error(t('errors.somethingWentWrong'), error)
  }
}

// Function to get Random Briefs for each Labels
const generateLocalLabelsAndBriefs = (): LabelBriefPair[] => {
  return Object.entries(MarketingLabelsBriefs).map(([label, briefs]) => {
    const randomIndex = Math.floor(Math.random() * briefs.length)
    return {
      label,
      brief: briefs[randomIndex],
    }
  })
}

const submitForm = async () => {
  if (!form.value) return
  if (!validateDiscount()) return

  const { valid } = await form.value.validate()
  if (!valid || !placementStore.selectedPublishedVariant) {
    console.error('Form validation failed.')
    return
  }

  try {
    isLoading.value = true
    if (
      (appStore.selectedDesignFlow.value === designFlows.upload.value ||
        appStore.selectedDesignFlow.value === designFlows.convert.value) &&
      creativeFile.value
    ) {
      const creativeExt = creativeFile.value.name.split('.').pop()
      const creativeUrl = await uploadToS3(
        creativeFile.value,
        s3Folders.uploadedCreatives,
        `creative-${Date.now()}.${creativeExt}`,
      )
      uploadedCreativeStore.setUploadedCreativeUrl(creativeUrl)
    }
    appStore.incrementStepperCount()

    switch (appStore.selectedDesignFlow.value) {
      case designFlows.generate.value:
        router.push({ name: 'Generate' })
        break
      case designFlows.convert.value:
        router.push({ name: 'Convert' })
        break
      case designFlows.upload.value:
        router.push({ name: 'Validate' })
        break
    }
  } catch (e: any) {
    const error = getErrorMessage(e);
    notify(error?.message || t('errors.somethingWentWrong'), "error")
  } finally {
    const discountInfo = {
      type: canvasStore.discountType,
      value: canvasStore.discountValue,
      couponCode: canvasStore.rawCouponCode,
      variation: canvasStore.discountVariation,
    }
    canvasStore.setBaseDiscountInfo(discountInfo)
    canvasStore.setLastUsedDiscountInfo(discountInfo)
    canvasStore.setDiscountInput(discountInfo)
    isLoading.value = false
  }
}

const handleFileChange = (newFile: File | File[] | null) => {
  creativeFileError.value = null

  if (!newFile) {
    creativeFile.value = null
    creativeFileError.value = t('errors.requiredField')
    return
  }

  const file = Array.isArray(newFile) ? newFile[0] : newFile
  if (!allowedTypes.includes(file.type)) {
    creativeFileError.value = t('errors.onlyImageFilesAllowed')
    creativeFile.value = null
    return
  }
  const fileSizeInMB = file.size / (1024 * 1024)
  if (fileSizeInMB > MAX_SIZE_MB) {
    creativeFileError.value = t('errors.RequestEntityVeryLarge')
    creativeFile.value = null
    return
  }
  creativeFile.value = file
  creativePreview.value = URL.createObjectURL(file)
}

const setDesignFlow = (option: DesignFlow) => {
  if (appStore.selectedDesignFlow.value === option.value) return
  if (option === designFlows.generate && brandStore.selectedBrand?.isCustomBrand) {
    skipSearchUpdate.value = true
    searchInput.value = ''
    menuOpen.value = false
    skipValidation.value = true
    brandStore.selectedBrand = null
    creativeFile.value = null
    creativeFileError.value = null
    creativePreview.value = null
    productStore.products = []
    productStore.baseProducts = []
    productStore.selectedProducts = []
  }
  appStore.setDesignFlow(option)
  if (
    option === designFlows.generate &&
    ((brandStore.selectedBrand &&
      productStore.products.length &&
      productStore.products[0].brand != brandStore.selectedBrand.name) ||
      (brandStore.selectedBrand && productStore.products.length == 0))
  ) {
    fetchProducts(brandStore.selectedBrand.name)
  }
}

const onMenuChange = (val: boolean) => {
  if (!val) {
    setTimeout(() => {
      productSearch.value = ''
    }, 250)
  }
}

const toggleBrandSelection = () => {
  brandName.value = ''
  selectedBrandLogo.value = null
  brandLogos.value = []
  showBrandAddition.value = !showBrandAddition.value
  brandUploadError.value = null
  creativeFileError.value = null
}
const addNewBrand = () => {
  const ids = brandStore.brands.map((b) => b.id)
  const id = Math.max(...ids) + 1
  const name = brandName.value
  const logoUrls = brandLogos.value.map((url) => {
    return { logoUrl: url, loading: false }
  })
  const brand: Brand = {
    id: id,
    name: name,
    logoUrls: logoUrls,
    colours: [],
    isCustomBrand: true,
  }
  brandStore.prependBrand(brand)
  brandStore.addedBrandsCount += 1
  selectBrand(brand)
  brandName.value = ''
  selectedBrandLogo.value = null
  brandLogos.value = []
  showBrandAddition.value = false
  menuOpen.value = false
}

const removeLogo = (logoUrl: string) => {
  brandLogos.value = brandLogos.value.filter((url) => url != logoUrl)
}

const removeFile = () => {
  creativeFile.value = null
  creativePreview.value = null
}
const triggerFileInput = () => {
  fileInput.value?.click()
}
const triggerCreativeFileInput = () => {
  fileInputCreative.value?.click()
}
const handleFileUpload = () => {
  if (selectedBrandLogo.value) {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/avif']
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'avif']
    brandUploadError.value = null
    const fileType = selectedBrandLogo.value.type
    const fileExtension = selectedBrandLogo.value.name.split('.').pop()?.toLowerCase()

    if (
      !allowedTypes.includes(fileType) ||
      !fileExtension ||
      !allowedExtensions.includes(fileExtension)
    ) {
      brandUploadError.value = t('errors.onlyImageFilesAllowed')
      selectedBrandLogo.value = null
      return
    }
    const fileSizeInMB = selectedBrandLogo.value.size / (1024 * 1024)
    if (fileSizeInMB > MAX_SIZE_MB) {
      brandUploadError.value = t('errors.RequestEntityVeryLarge')
      selectedBrandLogo.value = null
      return
    }
    fileError.value = null
    brandUploadError.value = null
    handleUpload(selectedBrandLogo.value)
    selectedBrandLogo.value = null
  }
}

const handleUpload = async (file: File | null) => {
  if (!file) return
  isFileUploading.value = true
  const extension = file.name.split('.').pop()
  try {
    const brandLogoImage = await uploadToS3(
      file,
      s3Folders.logos,
      `logo-${Date.now()}.${extension}`,
      false,
      true,
    )
    brandLogos.value.push(brandLogoImage)
  } catch (e: any) {
    const error = getErrorMessage(e)
    notify(error?.message || t('errors.somethingWentWrong'), "error")
    throw e
  } finally {
    isFileUploading.value = false
  }
}

const fetchProducts = async (brandName: string) => {
  if (appStore.selectedDesignFlow.value === designFlows.generate.value) {
    try {
      isProductsLoading.value = true
      productSearch.value = ''
      productStore.selectedProducts = []
      const products = await productService.getProducts(
        appStore.selectedBusinessProfileId,
        brandName,
      )
      productStore.setProducts(products)
      categoryPanel.value = [0]
    } catch (e) {
      console.log(e)
    } finally {
      isProductsLoading.value = false
    }
  }
}

const truncateBrand = (str: string, num: number) => {
  if (str.length > num) {
    return str.slice(0, num) + '...'
  } else {
    return str
  }
}

watch(isFormInvalid, (newVal) => {
  footerConfig.disableGenerate = newVal
})

watch(isLoading, (newVal) => {
  footerConfig.loadingGenerate = newVal
})

watch(autofixError, (newVal) => {
  footerConfig.showRetry = newVal
})

watch(disableRetry, (newVal) => {
  footerConfig.disableRetry = newVal
})

watch(
  () => selectedDiscountType.value,
  () => {
    setTimeout(() => discountValueField.value?.validate(), 0)
  },
)

watch(
  searchInput,
  debounce(() => {
    if (skipSearchUpdate.value) {
      skipSearchUpdate.value = false
      return
    }
    menuOpen.value = true
    brandStore.clearBrands()
    brandSearchTerm.value = searchInput.value
  }, 500),
)

watch(
  () => brandStore.selectedBrand,
  async (value) => {
    if (value) {
      fetchProducts(value.name)
    }
  },
)

watch(
  () => localLabelBrief.value,
  (value) => {
    const labelBrief = {
      ...value,
      brief: value.brief,
    }
    brandStore.setActiveLabel(labelBrief)
  },
)

watch(selectedAdSizeCreativeType, async (creativeType) => {
  if (creativeType) {
    await setPlacement(creativeType)
    const selectedPlacementId = placementStore.selectedPlacement?.creativeSetConfiguration.meta.id;
    if(!selectedPlacementId) throw new Error("Cannot set the config")
    await adSizeConfigStore.loadConfig(selectedPlacementId)
  }
})

onMounted(async () => {
  setFooterConfig({
    showGenerate: true,
    showRetry: false,
    disableGenerate: isFormInvalid.value,
    loadingGenerate: isLoading.value,
    onGenerate: submitForm,
  })
  appStore.setTraceSessionId()
  brandStore.resetBrandState()
  placementStore.resetPlacementState()
  productStore.resetProductState()
  enhancementStore.resetEnhancementState()
  canvasStore.resetCanvasState()
  canvasStore.resetDiscountInfo()
  uploadedCreativeStore.resetUploadedCreativeState()
  creativeStore.resetCreativeState()
  brandStore.clearLabelsAndSelection()
  const currentProfile = appStore.businessProfiles.find(
    (profile) => profile.id === appStore.selectedBusinessProfileId,
  )
  appStore.setProfileCurrency(
    getSymbolFromCurrency(currentProfile?.currencyCode || '') || currentProfile?.currencySymbol || '₹',
  )
  autofixStore.resetAutofixState()

  const labelBriefPairs = generateLocalLabelsAndBriefs()
  brandStore.setLabelsBriefs(labelBriefPairs || [])
  brandStore.setActiveLabel({ label: 'Brand Message', brief: '' })

  if (appStore.stepperCount != 0) {
    appStore.setStepperCount(0)
  }
  const businessProfile = appStore.businessProfiles.find(
    (bp) => bp.id === appStore.selectedBusinessProfileId,
  )
  if (!businessProfile) {
    console.error('No business profile found')
    return
  }
  appStore.setSelectedBusinessProfileLogo(businessProfile.thumbnailLogo)
  applyTheme(theme, businessProfile.bpCSS)
  const placementTypes = await placementService.getPlacements(businessProfile.id)
  placementStore.setPlacements(placementTypes)
  const avilableAdSizes = await adSizeConfigStore.fetchAvailableAdSizes()
  adSizes.value = avilableAdSizes.filter((size) => size.creativeType !== "SILB")
  const l2BannerAdSize = avilableAdSizes.find(size => size.creativeType === "L2 Banner")

  if (l2BannerAdSize) {
    selectedAdSizeCreativeType.value = l2BannerAdSize.creativeType
    setPlacement(selectedAdSizeCreativeType.value)
    await adSizeConfigStore.loadConfig(l2BannerAdSize.id)
  }

  brandStore.setFilter({ processingStatus: ProductProcessingStatus.ReadyForUpload })
  brandStore.currentPageNumber += 1
  const searchParams = {
    ...brandStore.filters,
    pageNumber: brandStore.currentPageNumber,
  }
  const { brands, totalFilteredCount } = await brandService.getBrands(
    appStore.selectedBusinessProfileId,
    searchParams,
  )
  brandStore.appendBrands(brands)
  brandStore.totalBrandsCount = totalFilteredCount

  if (appStore.embedUser && appStore.embedUser?.brandId) {
    const brand = brandStore.brands.find((b) => b.id == appStore.embedUser?.brandId)
    if (brand) {
      selectBrand(brand)
    }
  }
})

onUnmounted(() => {
  setFooterConfig({
    showGenerate: false,
    disableGenerate: true,
    loadingGenerate: false,
    onNext: () => {},
  })
})
</script>
<style scoped>
:deep(.v-slide-group__container) {
  padding: 8px !important;
}

.logo-text {
  color: gray;
  font-weight: 600;
  font-size: 20px;
}

:deep(.v-badge__badge:has(.v-icon)) {
  padding: 0px !important;
  left: calc(100% - 18px) !important;
}

.bannerClass :deep(.v-field__append-inner) {
  display: none !important;
}

:deep(.v-list) {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.v-infinite-scroll {
  min-height: 87px;
}

:deep(.brands_selection .v-input__details) {
  min-height: 18px !important;
  padding-bottom: 2px;
  padding-top: 0px !important;
}

.brands-list {
  max-height: 40vh;
}

.product_quantitiy {
  line-height: 1.1rem;
}

.product-details {
  width: 100px;
}

.products-list {
  max-height: 290px;
  overflow: scroll;
}

:deep(.labels_loading_indicator .v-skeleton-loader__chip) {
  margin: 0 !important;
}

:deep(.category-list .v-expansion-panel-text__wrapper) {
  padding: 0 !important;
}

/* :deep(.custom-brand .v-input__details) {
  min-height: 0px;
} */
</style>
